import React from 'react';
import { Box, Typography, Popover, Radio, RadioGroup, FormControlLabel, Button, Slider, TextField, Divider } from '@mui/material';
import { ArrowDownAZ, ArrowUpAZ } from 'lucide-react';
import Toggle from '../ui/toggle';
import { FilterParams } from '../transactions-table';

export type AmountFilterHandle = {
    applyNow: () => void;
    clear: () => void;
};

interface AmountFilterProps {
    anchorEl?: HTMLElement | null;
    open?: boolean;
    onClose?: () => void;
    min: number;
    max: number;
    onApply: (params: FilterParams) => React.SetStateAction<void>;
    currentSort?: {
        sortBy?: string;
        sortDirection?: 'ASC' | 'DESC';
    };
    currentAmountKeyword?: [number, number];
    // Renders the same sort/filter controls inline (no Popover/anchor)
    // for use inside a combined modal instead of an anchored popup.
    embedded?: boolean;
    // Hides this section's own Apply Now/Clear buttons - used when a
    // parent (the combined filter modal) drives Apply/Clear for every
    // section at once via the imperative handle below instead.
    hideActions?: boolean;
}

const AmountFilter = React.forwardRef<AmountFilterHandle, AmountFilterProps>(({
    anchorEl, open, onClose,
    min, max,
    onApply,
    currentSort,
    currentAmountKeyword,
    embedded = false,
    hideActions = false,
}, ref) => {
    const [filterByRange, setFilterByRange] = React.useState<boolean>(!!currentAmountKeyword);
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc' | ''>(
        currentSort?.sortBy === 'amount' ? (currentSort?.sortDirection === 'ASC' ? 'asc' : 'desc') : ''
    );
    const [range, setRange] = React.useState<[number, number]>(
        currentAmountKeyword || [min, max]
    );

    // What's typed in the min/max fields, kept as text - a number input
    // turned "-" (or "1.") into 0 on every keystroke, so a negative amount
    // couldn't be typed minus-first. The range only takes the value once the
    // text is a complete number; leaving the field tidies the text up.
    const [rangeText, setRangeText] = React.useState<[string, string]>(() => {
        const r = currentAmountKeyword || [min, max];
        return [String(r[0]), String(r[1])];
    });
    const setRangeAndText = (next: [number, number]) => {
        setRange(next);
        setRangeText([String(next[0]), String(next[1])]);
    };

    // Update state when props change
    React.useEffect(() => {
        setFilterByRange(!!currentAmountKeyword);
        setSortOrder(currentSort?.sortBy === 'amount' ? (currentSort?.sortDirection === 'ASC' ? 'asc' : 'desc') : '');
        setRangeAndText(currentAmountKeyword || [min, max]);
    }, [currentAmountKeyword, currentSort, min, max]);

    const PARTIAL_AMOUNT = /^-?\d*\.?\d{0,2}$/;
    const handleRangeTextChange = (index: 0 | 1, text: string) => {
        if (!PARTIAL_AMOUNT.test(text)) return;
        setRangeText(prev => (index === 0 ? [text, prev[1]] : [prev[0], text]));
        const value = Number(text);
        if (text !== '' && text !== '-' && text !== '.' && text !== '-.' && Number.isFinite(value)) {
            setRange(prev => (index === 0 ? [value, prev[1]] : [prev[0], value]));
        }
    };

    const handleClear = () => {
        setFilterByRange(false);
        setSortOrder('');
        setRangeAndText([min, max]);
        const params: any = { amountKeyword: undefined };
        // Only signal a sort reset if this column currently owns the sort -
        // otherwise leave whatever sort is active on another column alone.
        if (currentSort?.sortBy === 'amount') {
            params.sortBy = null;
        }
        onApply(params);
    }

    // A minimum above the maximum matches nothing - flagged on both fields,
    // and never applied.
    const rangeInvalid = filterByRange && range[0] > range[1];

    const handleApplyNow = () => {
        // In the combined (mobile) filter the parent applies every section at
        // once - an invalid range just leaves the current amount filter as it
        // is there; on its own, Apply Now is disabled (below).
        const params: any = rangeInvalid ? {} : {
            amountKeyword: filterByRange ? range : undefined,
        };
        // Only include sort parameters if a radio value is selected
        if (sortOrder) {
            params.sortBy = 'amount';
            params.sortDirection = sortOrder.toUpperCase() as 'ASC' | 'DESC';
        }
        onApply(params);
    };

    React.useImperativeHandle(ref, () => ({
        applyNow: handleApplyNow,
        clear: handleClear,
    }));

    const content = (
        <>
            {!embedded && <>
                <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Sort & Filter</Typography>
                <Divider sx={{ my: 1 }}/>
            </>}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography sx={{ fontSize: 14 }}>Sort By Amount</Typography>
                </Box>
            </Box>
            <RadioGroup value={sortOrder || ''} onChange={e => setSortOrder(e.target.value as 'asc' | 'desc')} sx={{ mb: 1, pl: 3 }}>
                <FormControlLabel 
                    value="asc" 
                    sx={{ height: 24 }}
                    control={<Radio size='small'/>} 
                    label={<Typography sx={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 1 }}><ArrowDownAZ size={14} />Sort lowest to highest</Typography>} 
                />
                <FormControlLabel 
                    value="desc" 
                    sx={{ height: 24 }}
                    control={<Radio size='small'/>} 
                    label={<Typography sx={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 1 }}><ArrowUpAZ size={14} />Sort highest to lowest</Typography>} 
                />
            </RadioGroup>
            <Divider sx={{ mb: 1 }}/>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Toggle checked={filterByRange} onChange={checked => setFilterByRange(checked)} />
                    <Typography sx={{ fontWeight: 500, fontSize: 15 }}>Filter By Amount Range</Typography>
                </Box>
            </Box>
            {filterByRange && (
                <Box mb={2} boxSizing="border-box">
                    <Box mx={1}>
                        <Slider
                            min={min}
                            max={max}
                            value={range}
                            onChange={(_, val) => setRangeAndText(val as [number, number])}
                            valueLabelDisplay="auto"
                            sx={{ flex: 1 }}
                        />
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <TextField
                            size="small"
                            inputProps={{ inputMode: "decimal" }}
                            value={rangeText[0]}
                            onChange={e => handleRangeTextChange(0, e.target.value)}
                            onBlur={() => setRangeAndText(range)}
                            onKeyPress={e => {
                                if (e.key === 'Enter' && !rangeInvalid) {
                                    handleApplyNow();
                                }
                            }}
                            error={rangeInvalid}
                            sx={{ width: 100, background: 'var(--color-page-background)', borderRadius: 2 }}
                            InputProps={{
                                startAdornment: <Typography sx={{ fontSize: 12 }}>$&nbsp;</Typography>,
                                style: { fontSize: 12, height: 30 }
                            }}
                        />
                        <TextField
                            size="small"
                            inputProps={{ inputMode: "decimal" }}
                            value={rangeText[1]}
                            onChange={e => handleRangeTextChange(1, e.target.value)}
                            onBlur={() => setRangeAndText(range)}
                            onKeyPress={e => {
                                if (e.key === 'Enter' && !rangeInvalid) {
                                    handleApplyNow();
                                }
                            }}
                            error={rangeInvalid}
                            sx={{ width: 100, background: 'var(--color-page-background)', borderRadius: 2 }}
                            InputProps={{ 
                                startAdornment: <Typography sx={{ fontSize: 12 }}>$&nbsp;</Typography>,
                                style: { fontSize: 12, height: 30 } 
                            }}
                        />
                    </Box>
                    {rangeInvalid && (
                        <Typography sx={{ fontSize: 12, color: '#d32f2f' }}>
                            The minimum can't be more than the maximum.
                        </Typography>
                    )}
                </Box>
            )}
            {!hideActions && (
                <Box display="flex" gap={1} mt={2}>
                    <Button
                        fullWidth
                        variant="contained"
                        sx={{ background: 'var(--color-primary)', color: '#fff', borderRadius: 2, fontWeight: 600, boxShadow: 'none', whiteSpace: 'nowrap', '&:hover': { background: 'var(--color-primary)' } }}
                        onClick={handleApplyNow}
                        disabled={rangeInvalid}
                    >
                        Apply Now
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', borderRadius: 2, fontWeight: 600, boxShadow: 'none', whiteSpace: 'nowrap', '&:hover': { background: 'var(--color-container-background)' } }}
                        onClick={handleClear}
                    >
                        Clear
                    </Button>
                </Box>
            )}
        </>
    );

    if (embedded) {
        return <Box>{content}</Box>;
    }

    return (
        <Popover
            open={!!open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{ sx: { borderRadius: 3, minWidth: 300, p: 2, boxShadow: 3 } }}
        >
            {content}
        </Popover>
    );
});

export default AmountFilter;
